/**
 * "Sign-in" on the employee's page: how they get into the app by name, and the two
 * things the office does about it, creating that login and resetting a forgotten
 * password. Shown to anyone who may change users; not to someone who signs in with the
 * portal, whose email flows are unchanged.
 */

import { useState } from 'react';

import { Card } from '@/components/ui';
import { useI18n } from '@/i18n';
import type { User } from '@/lib/api';

import { NameLoginDialog } from './NameLoginDialog';
import { signInActions, typedAsName } from './nameLogin';

export function SignInPanel({ user, isSelf }: { user: User; isSelf: boolean }) {
  const { t } = useI18n();
  const [mode, setMode] = useState<'create' | 'reset' | null>(null);
  const actions = signInActions(user, isSelf);

  if (user.portalAccess) return null;
  if (!actions.create && !actions.reset && !user.loginName) return null;

  return (
    <section>
      <h2 className="font-bold mb-3">{t('access.nameLogin.heading')}</h2>
      <Card>
        <div className="space-y-3">
          {user.loginName ? (
            <div>
              <div className="text-xs font-semibold text-muted">{t('access.nameLogin.loginName')}</div>
              <div className="font-mono text-sm font-semibold">{user.loginName}</div>
              <p className="mt-1 text-xs leading-relaxed text-muted">
                {typedAsName(user, user.loginName)
                  ? t('access.nameLogin.typedHint', { first: user.firstName, last: user.lastName })
                  : t('access.nameLogin.differentName', { loginName: user.loginName })}
              </p>
            </div>
          ) : (
            <p className="text-sm leading-relaxed text-muted">{t('access.nameLogin.none')}</p>
          )}

          <div className="flex flex-wrap gap-2">
            {actions.create ? (
              <button type="button" className="btn-primary" onClick={() => setMode('create')}>
                {t('access.nameLogin.create')}
              </button>
            ) : null}
            {actions.reset ? (
              <button type="button" className="btn-secondary" onClick={() => setMode('reset')}>
                {t('access.nameLogin.reset')}
              </button>
            ) : null}
          </div>
        </div>
      </Card>

      {mode ? <NameLoginDialog person={user} mode={mode} open onClose={() => setMode(null)} /> : null}
    </section>
  );
}
